import React, { useState, forwardRef, useImperativeHandle } from "react";
import { v4 as uuidv4 } from "uuid";
import { Tooltip } from "@mui/material";
import { GridRowModes } from "@mui/x-data-grid";

import { ReactComponent as CrossIcon } from "assets/img/svg/CrossIcon.svg";
import AButton from "views/pages/manage/common/form-fields-mui/AButton";
import ADataGrid from "views/pages/manage/common/data-grid/ADataGrid";
import AInput from "views/pages/manage/common/formFeilds/customInput/AInput";
import { AGridColDef } from "views/pages/manage/common/data-grid/ADataGridColDef";
import AAutoComplete from "views/pages/manage/common/form-fields-mui/AAutoComplete";
import IconButtonWrapper from "views/pages/manage/common/form-fields-mui/IconButtonWrapper";

import {
  successToast,
  failureToast,
} from "views/pages/manage/common/utils/methods/toasterFunctions/function";
import { numberDecimalLimitRegex } from "views/pages/manage/unAssigned/collectPayment/selectPaymentMode/components/constant";
import { formattedPrice } from "views/pages/manage/unAssigned/collectPayment/utils";

import { createPaymentSlabRow, updatePaymentSlabRow } from "../helper";

const NEW_EMPTY_ROW = {
  id: uuidv4(),
  studentsFrom: "",
  studentsTo: "",
  courseAttribute: "",
  courseAttributeValue: "",
  amount: "",
  isNew: true,
};

const PaymentSlabTable = forwardRef(
  (
    {
      rows,
      setRows,
      currentPage,
      onPaginationChange,
      careerOptions,
      courseAttributeOptions,
      courseAttributeValueMap,
      academicGroupOptions = [],
      slabId,
    },
    ref,
  ) => {
    const [editRowId, setEditRowId] = useState(null);
    const [rowForm, setRowForm] = useState({});
    const [savingRowId, setSavingRowId] = useState(null);
    const [rowModesModel, setRowModesModel] = useState({});

    // Add new row
    const handleAddRow = () => {
      if (editRowId) return; // Prevent adding if editing
      const newRow = { ...NEW_EMPTY_ROW, id: uuidv4() };
      setRows((prev) => [newRow, ...prev]);
      setEditRowId(newRow.id);
      setRowForm(newRow);
      setRowModesModel &&
        setRowModesModel((oldModel) => ({
          ...oldModel,
          [newRow.id]: {
            mode: GridRowModes.Edit,
            fieldToFocus: "studentsFrom",
          },
        }));
    };

    // Check if all required fields are filled for enabling Save
    const isRowValid =
      rowForm.studentsFrom &&
      !isNaN(rowForm.studentsFrom) &&
      rowForm.studentsTo &&
      !isNaN(rowForm.studentsTo) &&
      rowForm.career &&
      rowForm.academicGroupId &&
      rowForm.courseAttributeId &&
      rowForm.courseAttributeValueId &&
      rowForm.amount &&
      !isNaN(rowForm.amount);

    // Expose addRow to parent via ref
    useImperativeHandle(ref, () => ({
      addRow: handleAddRow,
    }));

    // Start editing
    const handleEdit = (row) => {
      if (editRowId)
        return failureToast("Please Delete or Save current changes"); // Only one row editable at a time
      setEditRowId(row.id);
      setRowForm(row);

      setRowModesModel((oldModel) => ({
        ...oldModel,
        [row.id]: { mode: GridRowModes.Edit, fieldToFocus: "studentsFrom" },
      }));
    };

    const resetRow = (rowId) => {
      setEditRowId(null);
      setRowForm({});
      setRowModesModel &&
        setRowModesModel((oldModel) => ({
          ...oldModel,
          [rowId]: { mode: GridRowModes.View, ignoreModifications: true },
        }));
    };

    // Save row
    const handleSave = async (rowId) => {
      // Validation
      if (isNaN(rowForm.studentsFrom) || rowForm.studentsFrom < 0)
        return failureToast("Invalid students From");
      if (!rowForm.studentsTo || isNaN(rowForm.studentsTo))
        return failureToast("Invalid students To");
      if (parseInt(rowForm.studentsTo) <= parseInt(rowForm.studentsFrom))
        return failureToast("Invalid range!");
      if (!rowForm.career) return failureToast("Please select Career");
      if (!rowForm.courseAttributeValueId)
        return failureToast("Please select Course Attribute Value");
      if (!rowForm.courseAttributeId)
        return failureToast("Please select Course Attribute");
      if (!rowForm.amount || isNaN(rowForm.amount))
        return failureToast("Please enter vaid amount");

      setSavingRowId(rowId);
      try {
        const isNewRow = rowForm.isNew;
        const payload = {
          countFrom: parseInt(rowForm.studentsFrom),
          countTo: parseInt(rowForm.studentsTo),
          career: rowForm.career,
          slabType: "ADMISSION_ADVISOR",

          courseAttributeId: rowForm.courseAttributeId,
          courseAttributeDispValue: rowForm.courseAttributeDispValue,
          courseAttributeDesc: rowForm.courseAttributeDesc,

          courseAttributeValueId: rowForm.courseAttributeValueId,
          courseAttributeValueDispValue: rowForm.courseAttributeValueDispValue,
          courseAttributeValueDesc: rowForm.courseAttributeValueDesc,

          amount: parseInt(rowForm.amount),
          academicGroupId: rowForm.academicGroupId,
          academicGroupDispValue: rowForm.academicGroupDispValue,
        };

        if (isNewRow) {
          // Create new row
          if (!slabId) {
            failureToast("Slab ID is required to create new row");
            return;
          }
          const response = await createPaymentSlabRow(slabId, payload);
          if (response?.id) {
            const updatedRow = {
              ...rowForm,
              id: response.id,
              isNew: false,
            };
            setRows((prev) =>
              prev.map((row) => (row.id === rowId ? updatedRow : row)),
            );
            successToast("Row created successfully");
            resetRow(rowId);
          }
        } else {
          // Update existing row
          const response = await updatePaymentSlabRow(rowId, payload);

          if (response?.id) {
            const updatedRow = {
              ...rowForm,
              isNew: false,
            };
            setRows((prev) =>
              prev.map((row) => (row.id === rowId ? updatedRow : row)),
            );
            successToast("Row updated successfully");
            resetRow(rowId);
          }
        }
      } catch (error) {
        // If error and this was a new row, remove it from the table
        if (rowForm.isNew) {
          setRows((prev) => prev.filter((row) => row.id !== rowId));
          setEditRowId(null);
          setRowForm({});
          setRowModesModel &&
            setRowModesModel((oldModel) => ({
              ...oldModel,
              [rowId]: { mode: GridRowModes.View, ignoreModifications: true },
            }));
        }
        console.error("Error saving row:", error);
        failureToast("Failed to save row");
      } finally {
        setSavingRowId(null);
      }
    };

    // Cancel edit
    const handleCancel = (rowId) => {
      if (rows.find((row) => row.id === rowId)?.isNew) {
        setRows((prev) => prev.filter((row) => row.id !== rowId));
      }
      setEditRowId(null);
      setRowForm({});
      setRowModesModel &&
        setRowModesModel((oldModel) => ({
          ...oldModel,
          [rowId]: { mode: GridRowModes.View, ignoreModifications: true },
        }));
    };

    // Handle field change
    const handleFieldChange = (key, value) => {
      let obj = {};
      if (key === "courseAttributeValue") {
        obj.courseAttributeValueId = value.value;
        obj.courseAttributeValueDispValue = value.label;
        obj.courseAttributeValueDesc = value.desc;
      } else if (key === "courseAttribute") {
        obj.courseAttributeId = value.value;
        obj.courseAttributeDispValue = value.label;
        obj.courseAttributeDesc = value.desc;
      } else if (key === "academicGroup") {
        obj.academicGroupId = value.value;
        obj.academicGroupDispValue = value.label;
      } else {
        obj[key] = value;
      }
      setRowForm((prev) => ({ ...prev, ...obj }));
    };

    // Only one columns array, no duplicates or nesting
    const columns = [
      new AGridColDef("id", "Effective sequence")
        .setMinWidth(180)
        .editable(false)
        .renderCellComponent((params) => {
          // If this is a new row (id is a uuid or isNew is true), show null
          if (
            params.row.isNew ||
            (typeof params.value === "string" && params.value.length === 36)
          ) {
            return "-";
          }
          return params.value ?? "-";
        }),

      new AGridColDef("studentsFrom", "No. of students from")
        .setMinWidth(180)
        .editable(true)
        .renderCellComponent((params) => params.value ?? "-")
        .renderEditCellComponent(() => (
          <AInput
            placeholder="Enter"
            value={rowForm?.studentsFrom}
            handler={(value) => {
              handleFieldChange("studentsFrom", value);
            }}
            regex={numberDecimalLimitRegex(3, 0)}
          />
        )),

      new AGridColDef("studentsTo", "No. of students to")
        .setMinWidth(150)
        .editable(true)
        .renderCellComponent((params) => params.value ?? "-")
        .renderEditCellComponent(() => (
          <AInput
            placeholder="Enter"
            value={rowForm?.studentsTo}
            handler={(value) => {
              handleFieldChange("studentsTo", value);
            }}
            regex={numberDecimalLimitRegex(3, 0)}
          />
        )),

      new AGridColDef("career", "Career")
        .setMinWidth(80)
        .editable(true)
        .renderCellComponent((params) => {
          const option = careerOptions.find((c) => c.label === params.value);
          return option ? option.label : (params.value ?? "-");
        })
        .renderEditCellComponent(() => (
          <div className="w-100">
            <AAutoComplete
              items={careerOptions}
              currentValue={
                careerOptions.find((c) => c.label === rowForm.career) || null
              }
              handleChange={(val) =>
                handleFieldChange("career", val?.label || "")
              }
              isMulti={false}
              height="36px"
            />
          </div>
        )),

      // Stream (Academic Group) column
      new AGridColDef("academicGroupId", "Stream")
        .setMinWidth(100)
        .editable(true)
        .renderCellComponent((params) => {
          const option = academicGroupOptions.find(
            (c) => c.value === params.value,
          );
          return option ? option.label : (params.value ?? "-");
        })
        .renderEditCellComponent(() => (
          <div className="w-100">
            <AAutoComplete
              items={academicGroupOptions}
              currentValue={
                academicGroupOptions.find(
                  (c) => c.value === rowForm.academicGroupId,
                ) || null
              }
              handleChange={(val) =>
                handleFieldChange("academicGroup", val || {})
              }
              isMulti={false}
              height="36px"
            />
          </div>
        )),

      new AGridColDef("courseAttributeDispValue", "Course Attribute")
        .setMinWidth(150)
        .editable(true)
        .renderCellComponent((params) => {
          const option = courseAttributeOptions.find(
            (c) => c.value === params.row.courseAttributeId,
          );
          return option ? option.label : (params.value ?? "-");
        })
        .renderEditCellComponent(() => (
          <div className="w-100">
            <AAutoComplete
              items={courseAttributeOptions}
              currentValue={
                courseAttributeOptions.find(
                  (c) => c.value === rowForm.courseAttributeId,
                ) || null
              }
              handleChange={(val) =>
                handleFieldChange("courseAttribute", val || {})
              }
              isMulti={false}
              height="36px"
            />
          </div>
        )),

      new AGridColDef("courseAttributeDesc", "Description")
        .setMinWidth(150)
        .editable(true)
        .renderCellComponent((params) => {
          const attr = courseAttributeOptions.find(
            (c) => c.value === params.row.courseAttributeId,
          );
          const desc = attr ? attr.desc : (params.value ?? "-");
          return (
            <Tooltip title={desc || "-"} placement="top" arrow>
              <span
                style={{
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  display: "inline-block",
                  maxWidth: 140,
                }}
              >
                {desc}
              </span>
            </Tooltip>
          );
        })
        .renderEditCellComponent(() => (
          <Tooltip title={rowForm.courseAttributeDesc}>
            <div className="ellipsis-text-container">
              {rowForm?.courseAttributeDesc}
            </div>
          </Tooltip>
        )),

      new AGridColDef("courseAttributeValueDispValue", "Course Attribute Value")
        .setMinWidth(200)
        .editable(true)
        .renderCellComponent((params) => {
          const option = courseAttributeValueMap.find(
            (c) => c.value === params.row.courseAttributeValueId,
          );
          return option ? option.label : (params.value ?? "-");
        })
        .renderEditCellComponent(() => (
          <div className="w-100">
            <AAutoComplete
              items={courseAttributeValueMap}
              currentValue={
                courseAttributeValueMap.find(
                  (c) => c.value === rowForm.courseAttributeValueId,
                ) || null
              }
              handleChange={(val) =>
                handleFieldChange("courseAttributeValue", val || {})
              }
              isMulti={false}
              height="36px"
            />
          </div>
        )),

      new AGridColDef("courseAttributeValueDesc", "Description")
        .setMinWidth(150)
        .editable(true)
        .renderCellComponent((params) => {
          const attr = courseAttributeValueMap.find(
            (c) => c.value === params.row.courseAttributeValueId,
          );
          const desc = attr ? attr.desc : (params.value ?? "-");
          return (
            <Tooltip title={desc || "-"} placement="top" arrow>
              <span
                style={{
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  display: "inline-block",
                  maxWidth: 140,
                }}
              >
                {desc}
              </span>
            </Tooltip>
          );
        })
        .renderEditCellComponent(() => (
          <Tooltip title={rowForm.courseAttributeValueDesc}>
            <div className="ellipsis-text-container">
              {rowForm?.courseAttributeValueDesc}
            </div>
          </Tooltip>
        )),

      new AGridColDef("amount", "Amount")
        .setMinWidth(130)
        .editable(true)
        .renderCellComponent((params) => (
          <span className="text-bold">{formattedPrice(params.value ?? 0)}</span>
        ))
        .renderEditCellComponent(() => (
          <AInput
            placeholder="Enter"
            value={rowForm?.amount}
            handler={(value) => {
              handleFieldChange("amount", value);
            }}
            regex={numberDecimalLimitRegex(8, 2)}
          />
        )),

      new AGridColDef("actions", "Actions", false)
        .setMinWidth(120)
        .pinColumnLast(true)
        .renderCellComponent((params) => {
          // If this row is being edited, show Save/Cancel
          if (editRowId === params.row.id) {
            return (
              <>
                <AButton
                  variant="outline"
                  className="no-ml-btn"
                  size="xs"
                  onClick={() => handleSave(params.row.id)}
                  disabled={savingRowId === params.row.id || !isRowValid}
                >
                  Save
                </AButton>

                {/* <button
                  className="btn btn-sm"
                  title="Cancel"
                  onClick={() => handleCancel(params.row.id)}
                  disabled={savingRowId === params.row.id}
                >
                </button> */}
                <IconButtonWrapper
                  onClick={() => handleCancel(params.row.id)}
                  disabled={savingRowId === params.row.id}
                >
                  <CrossIcon />
                </IconButtonWrapper>
              </>
            );
          }
          // If any row is being edited, disable Edit on others
          return (
            <button
              className="btn btn-primary btn-sm"
              onClick={() => handleEdit(params.row)}
              disabled={!!editRowId}
            >
              Edit
            </button>
          );
        }),
    ];

    // Render the table (assuming ADataGrid is the table component)
    return (
      <ADataGrid
        rows={rows}
        columns={columns}
        rowCount={rows.length}
        currentPage={currentPage}
        onPaginationChange={onPaginationChange}
        rowId={(row) => row.id}
        rowModesModel={rowModesModel}
        pinLastColumn
        hidePagination
        // onEditRowChange={setEditRowId}
        // onRowFormChange={setRowForm}
        // onSave={handleSave}
        // onCancel={handleCancel}
        // onAddRow={handleAddRow}
      />
    );
  },
);

export default PaymentSlabTable;
